import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import Router, { useRouter } from 'next/router';
import { TabLink as NavLink } from '@/Framework/Router/Next/Link';
import Select from '@/Framework/UI/Molecules/Form/Select/legacy';
import { useStaticTemplateContext } from '../StaticTemplateContext';
import useElementSize from '@/Framework/hooks/useElementSize';

import styles from './staticTemplateTabs.scss';

interface ITabs {
  id: string,
  path: string,
  name: string,
}

interface IProps {
  isTabsLabelExists?: boolean,
  tabs: Array<ITabs>
}

const StaticTemplateTabs = (props: IProps) => {
  const { pathname } = useRouter();
  const { tabsLabel } = useStaticTemplateContext();
  const [activeStateTab, setActiveStateTab] = useState(null);
  const [isFixed, setIsFixed] = useState(false);
  const tabsRef = useRef(null);
  const { height: tabsHeight } = useElementSize("tabsWrapper");
  const [indicatorLeftOffset, setIndicatorLeftOffset] = useState(0);

  const tabsOptions = useMemo(
    () => props.tabs.map((tab) => ({ label: tab.name, value: tab.path })).filter(Boolean),
    [props.tabs]
  );

  const tabsRefs = useMemo(
    () => props.tabs.reduce((result, tab) => ({ ...result, [tab.id]: React.createRef() }), {}),
    [props.tabs]
  );

  const setActiveTabIndicatorStyle = useCallback(() => {
    if (!tabsRefs || !activeStateTab) {
      return;
    }

    const tabElement = tabsRefs[activeStateTab.id].current;

    setIndicatorLeftOffset(tabElement.offsetLeft + tabElement.offsetWidth / 2 - 10);
  }, [tabsRefs, activeStateTab]);

  const throttlingUpdate = throttle(setActiveTabIndicatorStyle, 100);

  const scrollHandler = useCallback(() => {
    if (tabsRef.current?.getBoundingClientRect().top <= 0) {
      setIsFixed(true);
    } else {
      setIsFixed(false)
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    }
  }, [scrollHandler])

  useEffect(() => {
    throttlingUpdate()
    window.addEventListener('resize', throttlingUpdate);
    return () => {
      window.removeEventListener('resize', throttlingUpdate);
    };
  }, [throttlingUpdate]);

  const setActiveTab = () => {
    const activeTab = props.tabs.find((tab) => pathname.startsWith(tab.path));

    setActiveStateTab(activeTab);
  };

  useEffect(() => {
    setActiveTab();
  }, [pathname]);

  useEffect(() => {
    setActiveTabIndicatorStyle();
  }, [setActiveTabIndicatorStyle, activeStateTab]);

  const onSelectChange = useCallback((option) => {
    Router.push(option.value);
  }, [pathname]);

  const renderTab = useCallback((tab) => {
    return (
      <NavLink
        key={ tab.id }
        id={ tab.id }
        to={ tab.path }
        className={ styles.tab }
        activeClassName={ cn(styles.tab, styles.activeTabLink) }
        ref={ tabsRefs[tab.id] }
        dataTest={ tab.name }
      >
        { tab.name }
      </NavLink>
    );
  }, [tabsRefs]);

  const tabSelect = (
    <Select
      name="tabs"
      className={ styles.selectTabs }
      value={ {
        value: activeStateTab?.path,
        label: activeStateTab?.name
      } }
      options={ tabsOptions }
      onChange={ onSelectChange }
      searchable={ false }
      isNarrow
      dataTest="staticTemplateTabSecondSelect"
    />
  );

  return (
    <div id="tabsWrapper" ref={ tabsRef }>
      { isFixed && <div style={ { height: tabsHeight} }/> }
      <div
          className={ cn(styles.stickyTabsWrapper, {
            [styles.stickyTabsWrapperFixed]: isFixed,
          }) }
      >
        <div className={ styles.tabsRow }>
          { props.tabs.map(renderTab) }
          { (indicatorLeftOffset !== 0) && (
            <div
              className={ styles.activeTabIndicator }
              style={ {
                left: `${ indicatorLeftOffset }px`
              } }
            />
          ) }
          { activeStateTab && (
            props.isTabsLabelExists && tabsLabel ? (
              <div className={ styles.tabsContainer }>
                <div className={ styles.tabsLabelContainer }>
                  <span className={ styles.tabsLabel }>{ tabsLabel }</span>
                </div>
                { tabSelect }
              </div>
            ) : ( tabSelect )
          ) }
        </div>
      </div>
    </div>
  );
};

export default StaticTemplateTabs;
